import {
  useCartAttributes,
  useEwalletPaymentMethods,
  useIsGuestCart,
  usePaymentMethods,
  useSetOrderSourceOnCart,
  useShippingMethods
} from "./cart.mjs";
import { useResetCart } from "./clear-cart.mjs";
import { useAlerts } from "../alerts.mjs";
import { topics, useChannels } from "../channels.mjs";
import { useExceptions } from "../exceptions.mjs";
import { PlaceOrderDocument } from "../../graphql/Cart.mjs";
import { orderCreatedRoute } from "../../utils/orders.mjs";
import { useAppRouter } from "../router.mjs";
import { useMutation } from "villus";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
function usePlaceOrder() {
  const { execute } = useMutation(PlaceOrderDocument);
  const { cartId } = useCartAttributes();
  async function placeOrder() {
    const { data, error } = await execute({
      input: {
        cart_id: cartId.value
      }
    });
    return {
      order: data?.response?.order,
      error
    };
  }
  return {
    placeOrder
  };
}
function useSetShippingMethod() {
  const { setShippingMethod: execute } = useShippingMethods();
  async function setShippingMethod(shippingMethod) {
    if (!shippingMethod) {
      throw new Error("No selected shipping method");
    }
    await execute({
      carrier_code: shippingMethod?.carrier_code,
      method_code: shippingMethod?.method_code
    });
  }
  return {
    setShippingMethod
  };
}
function useClassicPlaceOrder(options = computed(() => ({
  withIframeEnabled: false,
  withIframeQueryParam: true,
  cached: "cache-first"
}))) {
  const { placeOrder } = usePlaceOrder();
  const { setShippingMethod } = useSetShippingMethod();
  const { setPaymentMethod } = usePaymentMethods(options);
  const { resetCart } = useResetCart();
  const { redirect } = useAppRouter();
  const { isGuestCart } = useIsGuestCart();
  const { resolveException } = useExceptions("cart");
  async function classicPlaceOrder(paymentMethod, shippingMethod) {
    await setShippingMethod(shippingMethod);
    if (!paymentMethod) {
      throw new Error("No selected payment method");
    }
    await setPaymentMethod(paymentMethod.code);
    const { order, error } = await placeOrder();
    if (error && resolveException(error)?.level === "DANGER") {
      throw new Error(resolveException(error)?.message);
    }
    if (error || !order?.order_number) {
      throw new Error(error?.message);
    }
    resetCart();
    redirect(
      orderCreatedRoute(order?.order_number?.toString(), !!isGuestCart.value)
    );
  }
  return {
    classicPlaceOrder
  };
}
function usePaymobPlaceOrder() {
  const { setShippingMethod } = useSetShippingMethod();
  const { setPaymentMethod } = usePaymentMethods(
    computed(() => ({
      withIframeEnabled: true,
      cached: "cache-first",
      withIframeQueryParam: true
    }))
  );
  async function paymobPlaceOrder(paymentMethod, shippingMethod) {
    await setShippingMethod(shippingMethod);
    if (!paymentMethod) {
      throw new Error("No selected payment method");
    }
    const response = await setPaymentMethod(paymentMethod.code);
    if (!response?.cart.acceptIframeUrl) throw new Error("cannot place order");
    if (response?.cart.acceptIframeUrl)
      window.location.replace(response?.cart.acceptIframeUrl);
  }
  return {
    paymobPlaceOrder
  };
}
export function useEwalletPlaceOrder() {
  const { setPaymentMethod } = useEwalletPaymentMethods();
  async function eWalletPlaceOrder(phoneNumber) {
    if (!phoneNumber) {
      throw new Error("Phone number is not inserted");
    }
    const response = await setPaymentMethod(phoneNumber);
    if (!response?.cart.acceptIframeUrl)
      throw new Error("validation.walletNumberError");
    if (response?.cart.acceptIframeUrl)
      window.location.replace(response?.cart.acceptIframeUrl);
  }
  return {
    eWalletPlaceOrder
  };
}
const paymobPaymentMethods = [
  "robusta_accept_valu",
  "robusta_accept_sympl",
  "robusta_accept_cc",
  "robusta_accept_premium",
  "robusta_accept_installments",
  "robusta_accept_souhoola"
];
const classicPaymentMethods = ["cashondelivery", "cardondelivery", "free"];
function useApplicationPlaceOrder(classicalPaymentMethod, options = computed(() => ({
  withIframeEnabled: false,
  withIframeQueryParam: true,
  cached: "cache-first"
}))) {
  const state = ref("idle");
  const { classicPlaceOrder } = useClassicPlaceOrder(options);
  const { setShippingMethod } = useShippingMethods();
  const { paymobPlaceOrder } = usePaymobPlaceOrder();
  const { push } = useChannels();
  async function placeOrder(selectedPaymentMethod, shippingMethod) {
    if ([...classicalPaymentMethod || [], ...classicPaymentMethods].includes(
      selectedPaymentMethod?.value?.code || ""
    )) {
      try {
        state.value = "loading";
        await classicPlaceOrder(
          selectedPaymentMethod.value,
          shippingMethod.value
        );
      } catch (e) {
        state.value = "error";
        throw e;
      }
      return;
    }
    if (paymobPaymentMethods.includes(selectedPaymentMethod.value?.code)) {
      try {
        state.value = "loading";
        await paymobPlaceOrder(
          selectedPaymentMethod.value,
          shippingMethod.value
        );
        return;
      } catch (e) {
        state.value = "error";
      }
    }
    if (selectedPaymentMethod.value?.code === "robusta_accept_mobile_wallets") {
      try {
        state.value = "loading";
        await setShippingMethod({
          carrier_code: shippingMethod.value?.carrier_code || "",
          method_code: shippingMethod.value?.method_code || ""
        });
        push(topics.cart.placeOrder);
        return;
      } catch (e) {
        state.value = "error";
      }
    }
    if (selectedPaymentMethod.value?.code === "robusta_payfort_cc") {
      try {
        state.value = "loading";
        await setShippingMethod({
          carrier_code: shippingMethod.value?.carrier_code || "",
          method_code: shippingMethod.value?.method_code || ""
        });
        push(topics.cart.placeOrder);
        return;
      } catch (e) {
        state.value = "error";
      }
    }
    state.value = "error";
    throw new Error(" Payment method is not supported ");
  }
  return {
    placeOrder,
    state
  };
}
export function useCheckoutShippingMethod() {
  const {
    setShippingMethod,
    shippingMethods,
    isFetching: isUpdatingShippingMethod
  } = useShippingMethods();
  return {
    shippingMethods,
    setShippingMethod,
    isUpdatingShippingMethod
  };
}
export function useCheckoutPaymentMethod(options = computed(() => ({
  withIframeEnabled: false,
  withIframeQueryParam: true,
  cached: "cache-first"
}))) {
  const {
    setPaymentMethod,
    paymentMethods,
    isFetching: isUpdatingPaymentState
  } = usePaymentMethods(options);
  return {
    paymentMethods,
    setPaymentMethod,
    isUpdatingPaymentState
  };
}
export function useCheckout(options = computed(() => ({
  withIframeEnabled: false,
  withIframeQueryParam: true,
  cached: "cache-first"
})), additionalPaymentMethods) {
  const { isUpdatingPaymentState, paymentMethods, setPaymentMethod } = useCheckoutPaymentMethod(options);
  const { isUpdatingShippingMethod, shippingMethods, setShippingMethod } = useCheckoutShippingMethod();
  const { error } = useAlerts();
  const { t } = useI18n();
  const { placeOrder, state } = useApplicationPlaceOrder(
    additionalPaymentMethods,
    options
  );
  const { addOrderSourceOnCart } = useSetOrderSourceOnCart();
  const isPlacingOrder = ref(false);
  const { subscribe } = useChannels();
  const subscriptionHandler = ref();
  async function submitCheckout(selectedPaymentMethod, selectedShippingMethod) {
    try {
      await addOrderSourceOnCart();
      await placeOrder(
        selectedPaymentMethod,
        selectedShippingMethod
      );
    } catch (e) {
      error("error", t(e.message).toString());
    }
  }
  async function resetErrors() {
    state.value = "idle";
  }
  if (!subscriptionHandler.value)
    subscriptionHandler.value = subscribe(topics.cart.placingOrderError, () => {
      state.value = "error";
    });
  const blacklist = ["storepickup"];
  return {
    setPaymentMethod,
    setShippingMethod,
    paymentMethods,
    shippingMethods: computed(
      () => shippingMethods.value.filter(
        (method) => !blacklist.includes(method.method_code || "")
      )
    ),
    /**
     * aggregate loading state to block the user when its updating payment or its fetching shipping method
     */
    loading: computed(() => state.value === "loading"),
    isUpdatingPaymentState,
    isUpdatingShippingMethod,
    isInvalidOptions: computed(() => state.value === "error"),
    submitCheckout,
    isPlacingOrder,
    resetErrors
  };
}
